import api from './axios';
import {
  API_ENDPOINTS,
  DEFAULT_BASE_SUM_ASSURED,
  DEFAULT_POLICY_TERM,
  DEFAULT_PREMIUM_PAYING_TERM,
  PREMIUM_PAYMENT_MODES,
  DEFAULT_LIMITED_PAY_OPTIONS,
  PAYMENT_TERM_TYPE,
  PLAN_NAMES,
} from '../constants';
import { logError, logDebug } from '../common/utils/logger';
import { encryptJourneyQueryValue } from '../common/utils/journeyEncryption';
// Re-export constants for backward compatibility
export { PREMIUM_PAYMENT_MODES, DEFAULT_LIMITED_PAY_OPTIONS, PAYMENT_TERM_TYPE };

/** Set VITE_DEBUG_PREMIUM=true in .env to trace multi-premium calls (verbose). */
const DEBUG_PREMIUM_MULTIPLEX =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEBUG_PREMIUM === 'true';

const MULTI_DIMENSION_PREMIUM_CACHE_TTL_MS = 2 * 60 * 1000;
const multiDimensionPremiumResultCache = new Map();
const multiDimensionPremiumInFlightCache = new Map();



const normalizeYesNo = (value) => {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim().toLowerCase();
  if (raw === 'yes' || raw === 'true' || raw === '1') return 'Yes';
  if (raw === 'no' || raw === 'false' || raw === '0') return 'No';
  return value;
};

const getAgeFromDob = (dob) => {
  if (!dob) return undefined;
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDelta = today.getMonth() - date.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < date.getDate())) {
    age -= 1;
  }
  if (age <= 0) return undefined;
  return age;
};

const stableSerialize = (value) => {
  if (value === null || value === undefined) return String(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

const cloneSerializable = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const getCachedMultiDimensionPremiumResult = (cacheKey) => {
  const cachedEntry = multiDimensionPremiumResultCache.get(cacheKey);
  if (!cachedEntry) return null;
  if (cachedEntry.expiresAt <= Date.now()) {
    multiDimensionPremiumResultCache.delete(cacheKey);
    return null;
  }
  return cloneSerializable(cachedEntry.result);
};

const setCachedMultiDimensionPremiumResult = (cacheKey, result) => {
  multiDimensionPremiumResultCache.set(cacheKey, {
    result,
    expiresAt: Date.now() + MULTI_DIMENSION_PREMIUM_CACHE_TTL_MS,
  });
};

const resolveState = (leadInput = {}) =>
  leadInput.branchLocationState ||
  leadInput.communicationAddressState ||
  leadInput.state ||
  undefined;

const buildPremiumDefaults = (premiumDefaults = {}) => ({
  premiumFrequency: premiumDefaults.premiumPaymentMode || 'Yearly',
  premiumPaymentMode: premiumDefaults.premiumPaymentMode || 'Yearly',
  ecs: premiumDefaults.ecs || 'Yes',
  channel: premiumDefaults.channel || 'Online',
  enhancedCoverageBenefit: premiumDefaults.enhancedCoverageBenefit || 'No',
  accidentalDeathBenefitRider: premiumDefaults.accidentalDeathBenefitRider || 'No',
  accidentalDeathAndDisabilityRider: premiumDefaults.accidentalDeathAndDisabilityRider || 'No',
  accidentalDeathAndDisabilityPlusRider: premiumDefaults.accidentalDeathAndDisabilityPlusRider || 'No',
  criticalIllnessRider: premiumDefaults.criticalIllnessRider || 'No',
});

/** Frontend rider IDs used in Quote UI */
const RIDER_IDS = {
  ADB: 'accidental-death',
  ADD: 'accidental-death-disability',
  ADD_PLUS: 'accidental-death-disability-plus',
  CI: 'critical-illness',
};

/**
 * Builds overrides for ROP and ECBO (Scenarios 1–3). Default NO.
 * @param {Object} quoteOptions - { premiumRefundEnabled: boolean, enhancedCoverageEnabled: boolean }
 * @param {boolean} _legacyIsEliteEligible - unused; ECBO is driven by selected plan (Plus or Elite)
 * @param {string} selectedPlan - active plan display name
 * @returns {{ returnOfPremium?: number, enhancedCoverageBenefit: string }}
 */
export const buildRopEcboOverrides = (quoteOptions = {}, legacyIsEliteEligible = false, selectedPlan = null) => {
  void legacyIsEliteEligible;
  // ROP cell exists for Plus workbook only; keep Elite without ROP payload.
  const rop = selectedPlan === PLAN_NAMES.PLUS
    ? (quoteOptions.premiumRefundEnabled === true ? 'Life Plus' : 'Life')
    : undefined;
  const planSupportsEcbo =
    selectedPlan === PLAN_NAMES.PLUS || selectedPlan === PLAN_NAMES.ELITE;
  const ecbo = planSupportsEcbo
    ? (quoteOptions.enhancedCoverageEnabled === true ? 'Yes' : 'No')
    : 'Doesn\'t meet eligibility';
  return { returnOfPremium: rop, enhancedCoverageBenefit: ecbo };
};

/**
 * Builds premium request overrides for rider selection, cover amounts (SA), and rider Policy Term / PPT.
 * Matches calculation sheet: riderADBEnabled, riderADBSA, riderADBPolicyTerm, riderADBPaymentTerm, etc.
 * Output rider premiums (riderADBPremium, riderADDPremium, riderADDPlusPremium, riderCIPremium) come from API.
 *
 * @param {Set<string>|string[]} selectedRiderIds - Selected rider IDs (e.g. from Quote)
 * @param {Object.<string, number>} riderCovers - Rider ID to cover amount (SA)
 * @param {number} [policyTerm] - Base policy term (years); used for rider PT when provided
 * @param {string} [paymentTermType] - 'Limited Pay' or 'Regular Pay'
 * @param {number} [selectedLimitedPayTerm] - Selected LP years for the current plan when paymentTermType is Limited Pay
 * @returns {Object} Overrides to merge into premium request
 */
export const buildRiderOverridesFromSelection = (
  selectedRiderIds = [],
  riderCovers = {},
  policyTerm = undefined,
  paymentTermType = undefined,
  selectedLimitedPayTerm = undefined,
) => {
  const set = selectedRiderIds instanceof Set ? selectedRiderIds : new Set(selectedRiderIds || []);
  const has = (id) => set.has(id);
  const cover = (id) => riderCovers[id] != null ? Number(riderCovers[id]) : undefined;
  const isLP = paymentTermType === 'Limited Pay';
  const riderPPT = policyTerm != null ? (isLP ? (selectedLimitedPayTerm ?? 5) : policyTerm) : undefined;
  const riderPT = policyTerm;

  return {
    accidentalDeathBenefitRider: has(RIDER_IDS.ADB) ? 'Yes' : 'No',
    accidentalDeathAndDisabilityRider: has(RIDER_IDS.ADD) ? 'Yes' : 'No',
    accidentalDeathAndDisabilityPlusRider: has(RIDER_IDS.ADD_PLUS) ? 'Yes' : 'No',
    criticalIllnessRider: has(RIDER_IDS.CI) ? 'Yes' : 'No',
    riderADBEnabled: has(RIDER_IDS.ADB) ? 'Yes' : 'No',
    riderADBSA: has(RIDER_IDS.ADB) ? cover(RIDER_IDS.ADB) : undefined,
    riderADBPolicyTerm: has(RIDER_IDS.ADB) && riderPT != null ? riderPT : undefined,
    riderADBPaymentTerm: has(RIDER_IDS.ADB) && riderPPT != null ? riderPPT : undefined,
    riderADDEnabled: has(RIDER_IDS.ADD) ? 'Yes' : 'No',
    riderADDSA: has(RIDER_IDS.ADD) ? cover(RIDER_IDS.ADD) : undefined,
    riderADDPolicyTerm: has(RIDER_IDS.ADD) && riderPT != null ? riderPT : undefined,
    riderADDPaymentTerm: has(RIDER_IDS.ADD) && riderPPT != null ? riderPPT : undefined,
    riderADDPlusEnabled: has(RIDER_IDS.ADD_PLUS) ? 'Yes' : 'No',
    riderADDPlusSA: has(RIDER_IDS.ADD_PLUS) ? cover(RIDER_IDS.ADD_PLUS) : undefined,
    riderADDPlusPolicyTerm: has(RIDER_IDS.ADD_PLUS) && riderPT != null ? riderPT : undefined,
    riderADDPlusPaymentTerm: has(RIDER_IDS.ADD_PLUS) && riderPPT != null ? riderPPT : undefined,
    riderCIEnabled: has(RIDER_IDS.CI) ? 'Yes' : 'No',
    riderCISA: has(RIDER_IDS.CI) ? cover(RIDER_IDS.CI) : undefined,
    riderCIPolicyTerm: has(RIDER_IDS.CI) && riderPT != null ? riderPT : undefined,
    riderCIPaymentTerm: has(RIDER_IDS.CI) && riderPPT != null ? riderPPT : undefined,
  };
};

/**
 * Resolve lead input from leadResult so premium request has lifeAssuredDob, lifeAssuredGender, etc.
 * When quoteInitResult is used (e.g. after product switch), it may not have leadInput; use top-level fields as fallback to avoid #VALUE! in Excel.
 */
const getEffectiveLeadInput = (leadResult) => {
  const leadInput = leadResult?.leadInput || {};
  const r = leadResult || {};
  return {
    lifeAssuredName: leadInput.lifeAssuredName ?? r.lifeAssuredName,
    lifeAssuredDob: leadInput.lifeAssuredDob ?? r.lifeAssuredDob,
    lifeAssuredAge: leadInput.lifeAssuredAge ?? r.lifeAssuredAge,
    lifeAssuredGender: leadInput.lifeAssuredGender ?? r.lifeAssuredGender,
    employeeFlag: leadInput.employeeFlag ?? r.employeeFlag,
    salaried: leadInput.salaried ?? r.salaried,
    smokerStatus: leadInput.smokerStatus ?? r.smokerStatus,
    branchLocationState: leadInput.branchLocationState ?? r.branchLocationState,
    communicationAddressState: leadInput.communicationAddressState ?? r.communicationAddressState,
    state: leadInput.state ?? r.state,
  };
};

/**
 * Resolve application number for premium APIs.
 * Lead/quote-init payloads often omit it after refresh or resume while Redux/session still have it.
 */
export const resolveApplicationNumberForPremium = (
  leadResult,
  overrides = {},
  journeyIdentifiers = {},
) => {
  const candidates = [
    overrides?.applicationNumber,
    leadResult?.applicationNumber,
    leadResult?.applicationNo,
    leadResult?.leadInput?.applicationNumber,
    leadResult?.quoteInitResult?.applicationNumber,
    journeyIdentifiers?.applicationNumber,
    journeyIdentifiers?.storedApplicationNumber,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed !== '') return trimmed;
  }
  return undefined;
};

export const buildPremiumRequestFromLead = (leadResult, overrides = {}) => {
  if (!leadResult) return null;
  const leadInput = getEffectiveLeadInput(leadResult);
  const premiumDefaults = leadResult.premiumDefaults || {};

  const derivedAge = getAgeFromDob(leadInput.lifeAssuredDob);

  const defaults = buildPremiumDefaults(premiumDefaults);

  // Resolve premiumPayingOption: 'Limited' or 'Regular' based on paymentTermType or override
  const premiumPayingOption = overrides.premiumPayingOption
    ?? (overrides.paymentTermType === 'Limited Pay' ? 'Limited' : 'Regular');

  return {
    applicationNumber: resolveApplicationNumberForPremium(leadResult, overrides),
    productCode: overrides.productCode ?? leadResult.assignedProductCode,
    lifeAssuredName: leadInput.lifeAssuredName,
    lifeAssuredDob: leadInput.lifeAssuredDob,
    lifeAssuredAge: derivedAge ?? leadInput.lifeAssuredAge,
    lifeAssuredGender: leadInput.lifeAssuredGender,
    baseSumAssured: overrides.baseSumAssured ?? DEFAULT_BASE_SUM_ASSURED,
    policyTerm: overrides.policyTerm ?? DEFAULT_POLICY_TERM,
    premiumPayingOption,
    premiumPayingTerm: overrides.premiumPayingTerm ?? DEFAULT_PREMIUM_PAYING_TERM,
    ...defaults,
    premiumFrequency: overrides.premiumFrequency ?? defaults.premiumFrequency,
    premiumPaymentMode: overrides.premiumFrequency ?? defaults.premiumPaymentMode,
    employeeLifeAssured: normalizeYesNo(leadInput.employeeFlag),
    branchLocationState: resolveState(leadInput),
    communicationAddressState: resolveState(leadInput),
    salaried: normalizeYesNo(leadInput.salaried),
    smokerStatus: normalizeYesNo(leadInput.smokerStatus),
    // ── Shared rider enable/SA fields (used by both Plus and Elite) ──
    riderADBEnabled: normalizeYesNo(overrides.riderADBEnabled),
    riderADBSA: overrides.riderADBSA ?? undefined,
    riderADDEnabled: normalizeYesNo(overrides.riderADDEnabled),
    riderADDSA: overrides.riderADDSA ?? undefined,
    riderADDPlusEnabled: normalizeYesNo(overrides.riderADDPlusEnabled),
    riderADDPlusSA: overrides.riderADDPlusSA ?? undefined,
    riderCIEnabled: normalizeYesNo(overrides.riderCIEnabled),
    riderCISA: overrides.riderCISA ?? undefined,

    // ── Elite plan rider term fields (segregated from Plus) ─────
    riderADBPolicyTerm: overrides.riderADBPolicyTerm ?? undefined,
    riderADBPaymentTerm: overrides.riderADBPaymentTerm ?? undefined,
    riderADDPolicyTerm: overrides.riderADDPolicyTerm ?? undefined,
    riderADDPaymentTerm: overrides.riderADDPaymentTerm ?? undefined,
    riderADDPlusPolicyTerm: overrides.riderADDPlusPolicyTerm ?? undefined,
    riderADDPlusPaymentTerm: overrides.riderADDPlusPaymentTerm ?? undefined,
    riderCIPolicyTerm: overrides.riderCIPolicyTerm ?? undefined,
    riderCIPaymentTerm: overrides.riderCIPaymentTerm ?? undefined,

    // ── Plus plan specific fields (segregated from Elite). ROP/ECBO default NO (Scenarios 1–3). ───────
    returnOfPremium: overrides.returnOfPremium ?? undefined,
    enhancedCoverageBenefit: overrides.enhancedCoverageBenefit ?? defaults.enhancedCoverageBenefit,
    riderPolicyTerm: overrides.riderPolicyTerm ?? undefined,
    riderPaymentTerm: overrides.riderPaymentTerm ?? undefined,
    riderCIPT: overrides.riderCIPT ?? undefined,
    riderCIPPT: overrides.riderCIPPT ?? undefined,
    premiumPaymentModeCode: overrides.premiumPaymentModeCode ?? undefined,
  };
};

const buildMultiDimensionPremiumRequestPayload = (leadResult, overrides = {}, options = {}) => {
  const baseRequest = buildPremiumRequestFromLead(leadResult, overrides);
  if (!baseRequest?.productCode) {
    throw new Error('Product code is missing for multi-dimension premium calculation.');
  }

  const {
    primaryMode = leadResult?.premiumDefaults?.premiumPaymentMode || 'Yearly',
    primaryTermType = PAYMENT_TERM_TYPE.REGULAR_PAY,
    primaryPremiumPayingTerm = null,
    limitedPayOptions = DEFAULT_LIMITED_PAY_OPTIONS,
    regularPayTerm = baseRequest.policyTerm || DEFAULT_POLICY_TERM,
    calculateAllPaymentTermTypes = true,
    returnAllResults = true,
  } = options;

  return {
    primaryMode,
    primaryTermType,
    requestPayload: {
      baseRequest,
      primaryPremiumPaymentMode: primaryMode,
      premiumPaymentModes: PREMIUM_PAYMENT_MODES,
      primaryPaymentTermType: primaryTermType,
      primaryPremiumPayingTerm: primaryPremiumPayingTerm || (primaryTermType === PAYMENT_TERM_TYPE.LIMITED_PAY ? limitedPayOptions[0] : regularPayTerm),
      limitedPayTermOptions: limitedPayOptions,
      regularPayTerm,
      calculateAllPaymentTermTypes,
      returnAllResults,
    },
  };
};

export const calculatePremiumFromLead = async (leadResult, overrides = {}) => {
  try {
    const payload = buildPremiumRequestFromLead(leadResult, overrides);
    if (!payload?.productCode) {
      throw new Error('Product code is missing for premium calculation.');
    }

    const response = await api.post(API_ENDPOINTS.CALCULATE_PREMIUM, payload);
    const data = response?.data ?? null;

    const tryParse = (value) => {
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    let parsed = tryParse(data);
    if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
      parsed = tryParse(parsed);
    }

    if (parsed && typeof parsed === 'object') {
      return {
        productName: leadResult?.assignedProductName,
        ...parsed,
      };
    }

    return { calculationSuccessful: true, productName: leadResult?.assignedProductName };
  } catch (error) {
    logError('[calculatePremiumFromLead] Failed to calculate premium', error);
    throw error;
  }
};

/**
 * Calculates premium for all dimensions (payment modes + payment term types) in a single API call.
 * This enables instant switching between payment modes and LP/RP options without additional API calls.
 *
 * @param {Object} leadResult - The lead/quote init result containing lead input and defaults
 * @param {Object} overrides - Optional overrides for the base request
 * @param {Object} options - Optional configuration options
 * @param {string} options.primaryMode - The primary/selected payment mode (default: from leadResult or 'Yearly')
 * @param {string} options.primaryTermType - 'LP' or 'RP' (default: 'RP')
 * @param {number} options.primaryPremiumPayingTerm - The selected premium paying term
 * @param {number[]} options.limitedPayOptions - LP term options to calculate (default: [5, 7, 10, 15])
 * @param {number} options.regularPayTerm - RP term value (default: policyTerm)
 * @param {boolean} options.calculateAllPaymentTermTypes - Whether to calculate both LP and RP (default: true)
 * @returns {Object} Multi-dimension result with primaryResult, allResults, limitedPayResults, regularPayResult
 */
export const calculateMultiDimensionPremium = async (leadResult, overrides = {}, options = {}) => {
  try {
    const { primaryMode, primaryTermType, requestPayload } = buildMultiDimensionPremiumRequestPayload(
      leadResult,
      overrides,
      options,
    );
    const baseRequest = requestPayload.baseRequest;
    const cacheKey = stableSerialize(requestPayload);

    if (DEBUG_PREMIUM_MULTIPLEX) {
      logDebug('[calculateMultiDimensionPremium] Calling API with:', {
        productCode: baseRequest.productCode,
        primaryMode,
        primaryTermType,
        limitedPayOptions: requestPayload.limitedPayTermOptions,
        regularPayTerm: requestPayload.regularPayTerm,
        calculateAllPaymentTermTypes: requestPayload.calculateAllPaymentTermTypes,
      });
    }

    const cachedResult = getCachedMultiDimensionPremiumResult(cacheKey);
    if (cachedResult) {
      if (DEBUG_PREMIUM_MULTIPLEX) {
        logDebug('[calculateMultiDimensionPremium] cache hit', {
          productCode: baseRequest.productCode,
        });
      }
      return cachedResult;
    }

    const inFlightRequest = multiDimensionPremiumInFlightCache.get(cacheKey);
    if (inFlightRequest) {
      if (DEBUG_PREMIUM_MULTIPLEX) {
        logDebug('[calculateMultiDimensionPremium] in-flight dedupe', {
          productCode: baseRequest.productCode,
        });
      }
      return inFlightRequest.then((result) => cloneSerializable(result));
    }

    const resultPromise = api.post(API_ENDPOINTS.CALCULATE_MULTI_PREMIUM, requestPayload)
      .then((response) => {
        const data = response?.data ?? null;

        const tryParse = (value) => {
          if (typeof value !== 'string') return value;
          try {
            return JSON.parse(value);
          } catch {
            return value;
          }
        };

        let parsed = tryParse(data);
        if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
          parsed = tryParse(parsed);
        }

        const buildRelevantPremiumError = (payload) => {
          const primary = payload?.primaryResult || {};
          const relevantCode =
            payload?.errorCode ||
            primary?.errorCode ||
            payload?.excelErrorCode ||
            primary?.excelErrorCode ||
            null;

          if (relevantCode) {
            return `Premium calculation failed: ${relevantCode}`;
          }

          const relevantMessage =
            payload?.errorMessage ||
            primary?.errorMessage ||
            null;

          if (typeof relevantMessage === 'string' && relevantMessage.trim()) {
            return relevantMessage.trim();
          }

          return 'Premium calculation could not be completed for the selected options.';
        };

        /** Reject empty / stub payloads so UI never shows ₹0 from a fake success (root cause of “broken” premium). */
        const rowHasPremiumFigure = (row) => {
          if (!row || typeof row !== 'object') return false;
          const n = (v) =>
            v != null && v !== '' && !Number.isNaN(Number(v));
          return (
            n(row.finalDiscountedPremium) ||
            n(row.totalPayableAmount) ||
            n(row.premiumAmount) ||
            n(row.finalPremium) ||
            n(row.annualPremium)
          );
        };

        const multiPayloadHasPremiumFigures = (payload) => {
          if (!payload || typeof payload !== 'object') return false;
          if (rowHasPremiumFigure(payload.primaryResult)) return true;
          if (rowHasPremiumFigure(payload.regularPayResult)) return true;
          const scanMap = (m) => {
            if (!m || typeof m !== 'object') return false;
            return Object.keys(m).some((k) => rowHasPremiumFigure(m[k]));
          };
          return (
            scanMap(payload.allResults) ||
            scanMap(payload.combinedResults) ||
            scanMap(payload.limitedPayResults)
          );
        };

        if (parsed && typeof parsed === 'object') {
          if (parsed.calculationSuccessful === false) {
            throw new Error(buildRelevantPremiumError(parsed));
          }

          if (!multiPayloadHasPremiumFigures(parsed)) {
            throw new Error(buildRelevantPremiumError(parsed));
          }

          const productName = leadResult?.assignedProductName;

          if (parsed.primaryResult) {
            parsed.primaryResult.productName = productName;
          }
          if (parsed.allResults) {
            Object.keys(parsed.allResults).forEach((mode) => {
              if (parsed.allResults[mode]) {
                parsed.allResults[mode].productName = productName;
              }
            });
          }
          if (parsed.limitedPayResults) {
            Object.keys(parsed.limitedPayResults).forEach((term) => {
              if (parsed.limitedPayResults[term]) {
                parsed.limitedPayResults[term].productName = productName;
              }
            });
          }
          if (parsed.regularPayResult) {
            parsed.regularPayResult.productName = productName;
          }
          if (parsed.combinedResults) {
            Object.keys(parsed.combinedResults).forEach((key) => {
              if (parsed.combinedResults[key]) {
                parsed.combinedResults[key].productName = productName;
              }
            });
          }

          if (DEBUG_PREMIUM_MULTIPLEX) {
            logDebug('[calculateMultiDimensionPremium] API response:', {
              calculationSuccessful: parsed.calculationSuccessful,
              calculationTimeMs: parsed.calculationTimeMs,
              primaryMode: parsed.primaryPremiumPaymentMode,
              primaryTermType: parsed.primaryPaymentTermType,
              modeResultCount: parsed.allResults ? Object.keys(parsed.allResults).length : 0,
              lpResultCount: parsed.limitedPayResults ? Object.keys(parsed.limitedPayResults).length : 0,
              combinedResultCount: parsed.combinedResults ? Object.keys(parsed.combinedResults).length : 0,
            });
          }

          setCachedMultiDimensionPremiumResult(cacheKey, parsed);
          return parsed;
        }

        throw new Error(
          'Premium service returned an empty or invalid response body.',
        );
      })
      .catch((error) => {
        const responseData = error?.response?.data;
        const message =
          (typeof responseData === 'object' && responseData !== null
            ? responseData.errorMessage || responseData.message
            : null) ||
          error?.message ||
          'Premium calculation could not be completed for the selected options.';

        logError('[calculateMultiDimensionPremium] Failed to calculate multi-dimension premium', { message });
        throw new Error(message);
      })
      .finally(() => {
        multiDimensionPremiumInFlightCache.delete(cacheKey);
      });

    multiDimensionPremiumInFlightCache.set(cacheKey, resultPromise);
    return resultPromise.then((result) => cloneSerializable(result));
  } catch (error) {
    logError('[calculateMultiDimensionPremium] Failed to prepare multi-dimension premium request', error);
    throw error;
  }
};

/**
 * Calculates premium for all (coverAmount, policyTerm) variations in a single API call.
 * Each variation returns a multi-dimension result for the requested payment mode(s) (default: primaryMode only)
 * plus LP/RP combinations for that mode. Pass options.premiumPaymentModes to override (e.g. all modes).
 * The frontend caches the response for the active frequency.
 *
 * @param {Object} leadResult - The lead/quote init result
 * @param {Object} overrides - Overrides for the base request (riders, ROP/ECBO, etc.)
 * @param {Array<{baseSumAssured: number, policyTerm: number}>} variations - All (SA, PT) combos to calculate
 * @param {Object} options - primaryMode, primaryTermType, limitedPayOptions, etc.
 * @returns {Object} { results: { "SA_PT": multiDimensionResult }, failedVariations, calculationSuccessful }
 */
export const calculateBulkPremium = async (leadResult, overrides = {}, variations = [], options = {}) => {
  try {
    const baseRequest = buildPremiumRequestFromLead(leadResult, overrides);
    if (!baseRequest?.productCode) {
      throw new Error('Product code is missing for bulk premium calculation.');
    }
    if (!variations.length) {
      throw new Error('At least one variation is required for bulk premium calculation.');
    }
    if (!baseRequest.applicationNumber || String(baseRequest.applicationNumber).trim() === '') {
      throw new Error('Application number is required for bulk premium calculation.');
    }

    const {
      primaryMode = leadResult?.premiumDefaults?.premiumPaymentMode || 'Yearly',
      primaryTermType = PAYMENT_TERM_TYPE.REGULAR_PAY,
      primaryPremiumPayingTerm = null,
      limitedPayOptions = DEFAULT_LIMITED_PAY_OPTIONS,
    } = options;

    const paymentModesForRequest = Array.isArray(options.premiumPaymentModes) && options.premiumPaymentModes.length
      ? options.premiumPaymentModes
      : [primaryMode];

    const encryptedBaseRequest = { ...baseRequest };
    if (encryptedBaseRequest.applicationNumber != null && String(encryptedBaseRequest.applicationNumber).trim() !== '') {
      encryptedBaseRequest.applicationNumber = encryptJourneyQueryValue(encryptedBaseRequest.applicationNumber);
    }
    if (encryptedBaseRequest.lifeAssuredDob != null && String(encryptedBaseRequest.lifeAssuredDob).trim() !== '') {
      encryptedBaseRequest.lifeAssuredDob = encryptJourneyQueryValue(encryptedBaseRequest.lifeAssuredDob);
    }

    const requestPayload = {
      baseRequest: encryptedBaseRequest,
      primaryPremiumPaymentMode: primaryMode,
      primaryPaymentTermType: primaryTermType,
      primaryPremiumPayingTerm: primaryPremiumPayingTerm || (primaryTermType === PAYMENT_TERM_TYPE.LIMITED_PAY ? limitedPayOptions[0] : variations[0]?.policyTerm || baseRequest.policyTerm),
      premiumPaymentModes: paymentModesForRequest,
      limitedPayTermOptions: limitedPayOptions,
      returnAllResults: true,
      variations: variations.map((v) => ({
        baseSumAssured: v.baseSumAssured,
        policyTerm: v.policyTerm,
      })),
    };

    if (DEBUG_PREMIUM_MULTIPLEX) {
      logDebug('[calculateBulkPremium] Calling API with:', {
        productCode: baseRequest.productCode,
        primaryMode,
        primaryTermType,
        variationCount: variations.length,
      });
    }

    const { signal } = options;
    const response = await api.post(API_ENDPOINTS.CALCULATE_BULK_PREMIUM, requestPayload, signal ? { signal } : undefined);
    const data = response?.data ?? null;

    const tryParse = (value) => {
      if (typeof value !== 'string') return value;
      try { return JSON.parse(value); } catch { return value; }
    };

    let parsed = tryParse(data);
    if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
      parsed = tryParse(parsed);
    }

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Bulk premium service returned an empty or invalid response body.');
    }

    if (parsed.calculationSuccessful === false && (!parsed.results || Object.keys(parsed.results).length === 0)) {
      throw new Error(parsed.errorMessage || 'Bulk premium calculation failed for all variations.');
    }

    const productName = leadResult?.assignedProductName;
    if (parsed.results) {
      Object.values(parsed.results).forEach((multiResult) => {
        if (!multiResult) return;
        if (multiResult.primaryResult) multiResult.primaryResult.productName = productName;
        if (multiResult.allResults) {
          Object.values(multiResult.allResults).forEach((r) => { if (r) r.productName = productName; });
        }
        if (multiResult.limitedPayResults) {
          Object.values(multiResult.limitedPayResults).forEach((r) => { if (r) r.productName = productName; });
        }
        if (multiResult.regularPayResult) multiResult.regularPayResult.productName = productName;
        if (multiResult.combinedResults) {
          Object.values(multiResult.combinedResults).forEach((r) => { if (r) r.productName = productName; });
        }
      });
    }

    if (DEBUG_PREMIUM_MULTIPLEX) {
      logDebug('[calculateBulkPremium] API response:', {
        calculationSuccessful: parsed.calculationSuccessful,
        calculationTimeMs: parsed.calculationTimeMs,
        resultCount: parsed.results ? Object.keys(parsed.results).length : 0,
        failedCount: parsed.failedVariations ? Object.keys(parsed.failedVariations).length : 0,
      });
    }

    return parsed;
  } catch (error) {
    const message =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      'Bulk premium calculation could not be completed.';
    logError('[calculateBulkPremium] Failed', { message });
    throw new Error(message);
  }
};

/**
 * Gets the cached premium result for a specific payment mode from multi-dimension results.
 * Falls back to primaryResult if the mode is not found.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - The payment mode to look up
 * @returns {Object} The premium calculation result for the specified mode
 */
export const getPremiumResultForMode = (multiDimensionResult, premiumPaymentMode) => {
  if (!multiDimensionResult) return null;

  // Try to get from allResults map first
  if (multiDimensionResult.allResults && multiDimensionResult.allResults[premiumPaymentMode]) {
    return multiDimensionResult.allResults[premiumPaymentMode];
  }

  // Fallback to primaryResult if mode matches or not found
  if (
    multiDimensionResult.primaryPremiumPaymentMode === premiumPaymentMode ||
    !multiDimensionResult.allResults
  ) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets the cached Limited Pay (LP) premium result for a specific term.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {number} premiumPayingTerm - The LP term to look up (5, 7, 10, 15)
 * @returns {Object} The premium calculation result for the specified LP term
 */
export const getLimitedPayResult = (multiDimensionResult, premiumPayingTerm) => {
  if (!multiDimensionResult) return null;

  // Try to get from limitedPayResults map
  if (multiDimensionResult.limitedPayResults) {
    // Handle both string and number keys (JSON may deserialize as strings)
    const term = String(premiumPayingTerm);
    if (multiDimensionResult.limitedPayResults[term]) {
      return multiDimensionResult.limitedPayResults[term];
    }
    if (multiDimensionResult.limitedPayResults[premiumPayingTerm]) {
      return multiDimensionResult.limitedPayResults[premiumPayingTerm];
    }
  }

  // Fallback to primaryResult if term type is LP and matches
  if (
    multiDimensionResult.primaryPaymentTermType === PAYMENT_TERM_TYPE.LIMITED_PAY &&
    multiDimensionResult.primaryPremiumPayingTerm === premiumPayingTerm
  ) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets the cached Regular Pay (RP) premium result.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @returns {Object} The premium calculation result for Regular Pay
 */
export const getRegularPayResult = (multiDimensionResult) => {
  if (!multiDimensionResult) return null;

  // Return regularPayResult if available
  if (multiDimensionResult.regularPayResult) {
    return multiDimensionResult.regularPayResult;
  }

  // Fallback to primaryResult if term type is RP
  if (multiDimensionResult.primaryPaymentTermType === PAYMENT_TERM_TYPE.REGULAR_PAY) {
    return multiDimensionResult.primaryResult;
  }

  return null;
};

/**
 * Gets all Limited Pay results as an array sorted by term.
 * Useful for displaying all LP options with their premiums.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @returns {Array<{term: number, result: Object}>} Array of LP results sorted by term
 */
export const getAllLimitedPayResults = (multiDimensionResult) => {
  if (!multiDimensionResult?.limitedPayResults) return [];

  return Object.keys(multiDimensionResult.limitedPayResults)
    .map((term) => ({
      term: Number(term),
      result: multiDimensionResult.limitedPayResults[term],
    }))
    .sort((a, b) => a.term - b.term);
};

/**
 * Gets a combined result for a specific combination of dimensions.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - The payment mode
 * @param {string} paymentTermType - 'LP' or 'RP'
 * @param {number} premiumPayingTerm - The premium paying term
 * @returns {Object} The premium calculation result for the combination
 */
export const getCombinedResult = (
  multiDimensionResult,
  premiumPaymentMode,
  paymentTermType,
  premiumPayingTerm
) => {
  if (!multiDimensionResult?.combinedResults) return null;

  const key = `${premiumPaymentMode}_${paymentTermType}_${premiumPayingTerm}`;
  return multiDimensionResult.combinedResults[key] || null;
};

/**
 * Gets the premium result based on payment term type selection.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} paymentTermType - 'LP' or 'RP'
 * @param {number} premiumPayingTerm - Required for LP, optional for RP
 * @returns {Object} The premium calculation result
 */
export const getPremiumResultForTermType = (multiDimensionResult, paymentTermType, premiumPayingTerm) => {
  if (!multiDimensionResult) return null;

  if (paymentTermType === PAYMENT_TERM_TYPE.LIMITED_PAY) {
    return getLimitedPayResult(multiDimensionResult, premiumPayingTerm);
  }

  return getRegularPayResult(multiDimensionResult);
};

/**
 * Gets the premium result for the full combination: frequency + payment term type + LP term.
 * Same dimension concept as backend: combinedResults key = "{mode}_{LP|RP}_{term}".
 * Prefer combinedResults so each option (e.g. LP 5/7/10/15) shows its own premium.
 *
 * @param {Object} multiDimensionResult - The result from calculateMultiDimensionPremium
 * @param {string} premiumPaymentMode - e.g. 'Yearly', 'Monthly', 'Quarterly', 'Half-yearly'
 * @param {string} paymentTermType - 'LP' or 'RP', or UI string 'Limited Pay' / 'Regular Pay'
 * @param {number|null} premiumPayingTerm - LP term (5, 7, 10, 15) or 0/null for RP
 * @returns {Object|null} The premium calculation result for that combination
 */
export const getPremiumResultForDimensions = (
  multiDimensionResult,
  premiumPaymentMode,
  paymentTermType,
  premiumPayingTerm
) => {
  if (!multiDimensionResult) return null;

  const isLP = paymentTermType === PAYMENT_TERM_TYPE.LIMITED_PAY || paymentTermType === 'Limited Pay';
  const termType = isLP ? PAYMENT_TERM_TYPE.LIMITED_PAY : PAYMENT_TERM_TYPE.REGULAR_PAY;
  const pptForKey = isLP ? (Number(premiumPayingTerm) || 0) : 0;

  const combined = getCombinedResult(multiDimensionResult, premiumPaymentMode, termType, pptForKey);
  if (combined) return combined;

  const termResult = getPremiumResultForTermType(
    multiDimensionResult,
    termType,
    termType === PAYMENT_TERM_TYPE.LIMITED_PAY ? premiumPayingTerm : undefined
  );
  if (termResult?.allResults?.[premiumPaymentMode]) return termResult.allResults[premiumPaymentMode];
  if (termResult && multiDimensionResult.primaryPremiumPaymentMode === premiumPaymentMode) return termResult;

  return getPremiumResultForMode(multiDimensionResult, premiumPaymentMode);
};
